import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { TrendingDown, Warning } from '@mui/icons-material';
import { formatNumber, formatConversionRate, getStageName } from '../../../../lib/utils/conversionFunnelUtils';
import { brandColors } from '../../../../theme/theme';

/**
 * DropoffAnalysisPanel Component
 * 
 * Analyzes where audiences are being lost in the funnel
 * Shows drop-offs by stage and segment with recommendations
 */
const DropoffAnalysisPanel = ({ demographicBreakdowns, funnelOverview }) => {
  if (!demographicBreakdowns || demographicBreakdowns.length === 0 || !funnelOverview) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No drop-off data available for analysis
        </Typography>
      </Paper>
    );
  }

  // Calculate drop-offs for each stage
  const calculateDropoffs = () => {
    const dropoffs = [];

    // For each stage (2-5), calculate drop-offs
    const stageKeys = [
      'stage_2_interested',
      'stage_3_willing_to_pay',
      'stage_4_theater',
      'stage_5_opening_weekend'
    ];

    const previousStageKeys = [
      'stage_1_aware',
      'stage_2_interested',
      'stage_3_willing_to_pay',
      'stage_4_theater'
    ];

    stageKeys.forEach((stageKey, index) => {
      const stage = funnelOverview[stageKey];
      const previousStage = funnelOverview[previousStageKeys[index]];
      
      if (stage && previousStage) {
        const dropped = previousStage.count - stage.count;
        const dropRate = previousStage.count > 0 ? (dropped / previousStage.count) * 100 : 0;
        
        dropoffs.push({
          stage: index + 2,
          fromStage: getStageName(index + 1),
          toStage: getStageName(index + 2),
          dropped,
          dropRate,
          remainingCount: stage.count
        });
      }
    });

    return dropoffs.sort((a, b) => b.dropped - a.dropped);
  };

  // Find segments with highest drop-offs for each stage
  const findSegmentDropoffs = () => {
    const segmentDropoffs = [];

    demographicBreakdowns.forEach(demo => {
      for (let i = 1; i < demo.stages.length; i++) {
        const currentStage = demo.stages[i];
        const previousStage = demo.stages[i - 1];

        Object.entries(currentStage.segments).forEach(([segment, metrics]) => {
          if (metrics.dropped_from_previous && metrics.drop_rate) {
            segmentDropoffs.push({
              demographic: demo.demographic,
              segment,
              stage: currentStage.stage,
              fromStage: getStageName(previousStage.stage),
              toStage: getStageName(currentStage.stage),
              dropped: metrics.dropped_from_previous,
              dropRate: metrics.drop_rate
            });
          }
        });
      }
    });

    return segmentDropoffs.sort((a, b) => b.dropped - a.dropped).slice(0, 10);
  };

  const overallDropoffs = calculateDropoffs();
  const segmentDropoffs = findSegmentDropoffs();

  // Find biggest problem area
  const biggestDropoff = overallDropoffs[0];

  // Get severity level
  const getDropoffSeverity = (rate) => {
    if (rate > 50) return { level: 'critical', color: brandColors.error, label: 'Critical' };
    if (rate > 35) return { level: 'high', color: brandColors.warning, label: 'High' };
    if (rate > 20) return { level: 'moderate', color: brandColors.info, label: 'Moderate' };
    return { level: 'low', color: brandColors.success, label: 'Low' };
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <TrendingDown sx={{ color: brandColors.error }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Drop-off Analysis
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Identify where audiences are being lost and which segments need attention
        </Typography>
      </Box>

      {/* Overall Drop-offs Summary */}
      <Box sx={{ mb: 3, p: 2, backgroundColor: `${brandColors.warning}10`, borderRadius: 2, border: `1px solid ${brandColors.warning}40` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Warning sx={{ color: brandColors.warning }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: brandColors.warning }}>
            Biggest Leakage Point
          </Typography>
        </Box>
        {biggestDropoff && (
          <Typography variant="body2">
            <strong>{formatNumber(biggestDropoff.dropped)}</strong> respondents ({formatConversionRate(biggestDropoff.dropRate)}) 
            drop off between <strong>{biggestDropoff.fromStage}</strong> and <strong>{biggestDropoff.toStage}</strong>.
            This represents the largest audience loss in your funnel.
          </Typography>
        )}
      </Box>

      {/* Overall Drop-offs Table */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Overall Funnel Drop-offs
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Transition</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Dropped</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Drop Rate</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Severity</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Remaining</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overallDropoffs.map((dropoff, index) => {
                const severity = getDropoffSeverity(dropoff.dropRate);
                return (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {dropoff.fromStage} → {dropoff.toStage}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ color: severity.color, fontWeight: 600 }}>
                        {formatNumber(dropoff.dropped)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ color: severity.color, fontWeight: 600 }}>
                        {formatConversionRate(dropoff.dropRate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={severity.label}
                        size="small"
                        sx={{
                          backgroundColor: `${severity.color}20`,
                          color: severity.color,
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatNumber(dropoff.remainingCount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Segment-Specific Drop-offs */}
      {segmentDropoffs.length > 0 && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Top 10 Segment Drop-offs
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Demographic</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Segment</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Transition</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Dropped</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Drop Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {segmentDropoffs.map((dropoff, index) => {
                  const severity = getDropoffSeverity(dropoff.dropRate);
                  return (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                          {dropoff.demographic.replace('_', ' ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {dropoff.segment}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {dropoff.fromStage} → {dropoff.toStage}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: severity.color, fontWeight: 600 }}>
                          {formatNumber(dropoff.dropped)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: severity.color, fontWeight: 600 }}>
                          {formatConversionRate(dropoff.dropRate)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Recommendations */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: `${brandColors.info}10`, borderRadius: 2, border: `1px solid ${brandColors.info}40` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: brandColors.info }}>
          Recommended Actions
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {overallDropoffs[0] && overallDropoffs[0].dropRate > 40 && (
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              <strong>Priority:</strong> Address the {overallDropoffs[0].fromStage} → {overallDropoffs[0].toStage} transition 
              with targeted campaigns to reduce the {formatConversionRate(overallDropoffs[0].dropRate)} drop-off.
            </Typography>
          )}
          {segmentDropoffs[0] && (
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              <strong>Focus Segment:</strong> {segmentDropoffs[0].segment} in {segmentDropoffs[0].demographic.replace('_', ' ')} 
              needs immediate attention with a {formatConversionRate(segmentDropoffs[0].dropRate)} drop rate.
            </Typography>
          )}
          <Typography component="li" variant="body2">
            <strong>Retention Strategy:</strong> Implement retargeting campaigns at high drop-off stages to recapture lost audiences.
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default DropoffAnalysisPanel;

